import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Plus, Image, MoreVertical, Trash2, Edit, X, Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';

const MyBoards = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchBoards();
  }, []);

  const fetchBoards = async () => {
    try {
      const response = await axios.get(`${API}/user-boards`, { withCredentials: true });
      setBoards(response.data);
    } catch (error) {
      console.error('Failed to fetch boards:', error);
    } finally {
      setLoading(false);
    }
  };

  const createBoard = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/user-boards`, formData, { withCredentials: true });
      setBoards([response.data, ...boards]);
      setShowCreate(false);
      setFormData({ name: '', description: '' });
      toast.success('Board created!');
    } catch (error) {
      toast.error('Failed to create board');
    }
  };

  const deleteBoard = async (boardId) => {
    if (!window.confirm('Delete this board and all saved items?')) return;
    try {
      await axios.delete(`${API}/user-boards/${boardId}`, { withCredentials: true });
      setBoards(boards.filter(b => b.board_id !== boardId));
      toast.success('Board deleted');
    } catch (error) {
      toast.error('Failed to delete board');
    }
  };

  const removeItem = async (boardId, itemId) => {
    try {
      await axios.delete(`${API}/user-boards/${boardId}/items/${itemId}`, { withCredentials: true });
      setBoards(boards.map(b => {
        if (b.board_id === boardId) {
          return { ...b, items: b.items.filter(i => i.item_id !== itemId) };
        }
        return b;
      }));
      if (selectedBoard?.board_id === boardId) {
        setSelectedBoard(prev => ({
          ...prev,
          items: prev.items.filter(i => i.item_id !== itemId)
        }));
      }
      toast.success('Item removed');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="max-w-[1800px] mx-auto px-6 md:px-12 py-12 pt-28">
        {/* Breadcrumb */}
        <Breadcrumb currentPage={t('nav.myBoards')} />
        
        {/* Title */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
          <div>
            <p className="caption-text text-muted-foreground mb-2">{t('dashboard.yourCollection')}</p>
            <h1 className="heading-display text-4xl md:text-5xl font-medium">{t('nav.myBoards')}</h1>
            <p className="text-muted-foreground mt-2">Save and organize your favorite inspirational images</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="btn-primary mt-6 md:mt-0 inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> {t('common.create')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="heading-display text-xl">{t('common.create')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={createBoard} className="space-y-4 mt-4">
                <div>
                  <Label className="caption-text mb-2 block">Board Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="input-field"
                    placeholder="e.g., Modern Kitchens"
                    required
                  />
                </div>
                <div>
                  <Label className="caption-text mb-2 block">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="input-field min-h-[80px]"
                    placeholder="What inspires this collection?"
                  />
                </div>
                <Button type="submit" className="btn-primary w-full">
                  Create Board
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Boards Grid */}
        {boards.length === 0 ? (
          <div className="space-y-12">
            {/* Empty State CTA */}
            <div className="text-center py-16 bg-gradient-to-b from-stone-50 to-white border border-stone-200">
              <Image className="w-16 h-16 mx-auto mb-4 text-stone-300" />
              <h3 className="heading-display text-xl font-medium mb-2">Crea la tua prima board</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Organizza i materiali per progetto, stanza o cliente. Condividi le tue selezioni e ottieni feedback.
              </p>
              <Button onClick={() => setShowCreate(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" /> Crea Board
              </Button>
            </div>
            
            {/* Example Boards - Inspiration */}
            <div>
              <h3 className="text-lg font-medium mb-4 text-stone-700">Esempi di board per ispirarti</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Example 1 */}
                <div className="group opacity-60 hover:opacity-80 transition-opacity cursor-not-allowed">
                  <div className="aspect-[4/3] bg-gradient-to-br from-stone-100 to-stone-200 mb-4 overflow-hidden relative">
                    <div className="grid grid-cols-2 gap-1 h-full">
                      <img src="https://margraf-cdn.thron.com/delivery/public/image/margraf/bbb1dad1-ad24-463f-a28c-ef1ff69d3305/huusih/std/800x0/calacatta.jpeg" alt="" className="w-full h-full object-cover" />
                      <img src="https://margraf-cdn.thron.com/delivery/public/image/margraf/272da0fe-0729-430e-a7fe-bbc0ba0ab8b0/huusih/std/800x0/bianco-statuario.jpg" alt="" className="w-full h-full object-cover" />
                      <img src="https://margraf-cdn.thron.com/delivery/public/image/margraf/a4ae1497-c22e-47d2-80b5-bb90e9c10968/huusih/std/800x0/arabescato.jpg" alt="" className="w-full h-full object-cover" />
                      <img src="https://margraf-cdn.thron.com/delivery/public/image/margraf/5a3e6d8a-3214-4c7b-9c88-1234567890ab/huusih/std/800x0/nero-marquina.jpg" alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute top-2 right-2 bg-stone-900/80 text-white text-xs px-2 py-1">
                      ESEMPIO
                    </div>
                  </div>
                  <h4 className="font-medium text-stone-800">Cucina Villa Moderna</h4>
                  <p className="text-sm text-muted-foreground">4 materiali • Cliente: Rossi</p>
                </div>
                
                {/* Example 2 */}
                <div className="group opacity-60 hover:opacity-80 transition-opacity cursor-not-allowed">
                  <div className="aspect-[4/3] bg-gradient-to-br from-amber-50 to-amber-100 mb-4 overflow-hidden relative">
                    <div className="grid grid-cols-2 gap-1 h-full">
                      <img src="https://margraf-cdn.thron.com/delivery/public/image/margraf/e0833c65-ff3a-4b64-8d46-789012345678/huusih/std/800x0/travertino-romano.jpeg" alt="" className="w-full h-full object-cover" />
                      <img src="https://margraf-cdn.thron.com/delivery/public/image/margraf/f1944d76-0049-4c75-9e57-890123456789/huusih/std/800x0/crema-marfil.jpeg" alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute top-2 right-2 bg-stone-900/80 text-white text-xs px-2 py-1">
                      ESEMPIO
                    </div>
                  </div>
                  <h4 className="font-medium text-stone-800">Bagno Master Suite</h4>
                  <p className="text-sm text-muted-foreground">2 materiali • In attesa approvazione</p>
                </div>
                
                {/* Example 3 */}
                <div className="group opacity-60 hover:opacity-80 transition-opacity cursor-not-allowed">
                  <div className="aspect-[4/3] bg-gradient-to-br from-stone-200 to-stone-300 mb-4 overflow-hidden relative flex items-center justify-center">
                    <div className="text-center">
                      <Plus className="w-10 h-10 text-stone-400 mx-auto mb-2" />
                      <span className="text-sm text-stone-500">Aggiungi materiali</span>
                    </div>
                    <div className="absolute top-2 right-2 bg-stone-900/80 text-white text-xs px-2 py-1">
                      ESEMPIO
                    </div>
                  </div>
                  <h4 className="font-medium text-stone-800">Nuova Board</h4>
                  <p className="text-sm text-muted-foreground">0 materiali • Bozza</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {boards.map((board) => (
              <div key={board.board_id} className="group">
                <div 
                  className="aspect-[4/3] bg-secondary mb-4 overflow-hidden relative cursor-pointer"
                  onClick={() => setSelectedBoard(board)}
                >
                  {board.cover_image ? (
                    <img 
                      src={board.cover_image} 
                      alt={board.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : board.items?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-1 h-full">
                      {board.items.slice(0, 4).map((item, idx) => (
                        <div key={idx} className="overflow-hidden bg-stone-warm/30">
                          <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Image className="w-12 h-12 text-muted-foreground/30 mb-2" />
                      <span className="text-xs text-muted-foreground/50 uppercase tracking-wider">Empty Board</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="bg-white px-4 py-2 text-xs uppercase tracking-wider font-medium">
                      View Board
                    </span>
                  </div>
                </div>
                
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="heading-display text-lg font-medium">{board.name}</h3>
                    {board.description && (
                      <p className="text-sm text-muted-foreground line-clamp-1">{board.description}</p>
                    )}
                    <span className="text-xs text-muted-foreground mt-1 block">
                      {board.items?.length || 0} items
                    </span>
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setSelectedBoard(board)}>
                        <Image className="w-4 h-4 mr-2" /> View
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => deleteBoard(board.board_id)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Board Detail Modal */}
        <Dialog open={!!selectedBoard} onOpenChange={() => setSelectedBoard(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="heading-display text-xl">{selectedBoard?.name}</DialogTitle>
              {selectedBoard?.description && (
                <p className="text-muted-foreground">{selectedBoard.description}</p>
              )}
            </DialogHeader>
            
            <div className="mt-4">
              {selectedBoard?.items?.length === 0 ? (
                <div className="text-center py-12">
                  <Image className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-muted-foreground">No images saved yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Browse the <Link to="/inspiration" className="text-primary hover:underline">Inspiration Gallery</Link> to find images to save.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {selectedBoard?.items?.map((item) => (
                    <div key={item.item_id} className="relative group">
                      <div className="aspect-square bg-secondary overflow-hidden">
                        <img 
                          src={item.image_url} 
                          alt={item.title || ''} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        onClick={() => removeItem(selectedBoard.board_id, item.item_id)}
                        className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                      >
                        <X className="w-4 h-4 text-destructive" />
                      </button>
                      {item.title && (
                        <p className="text-sm mt-2 truncate">{item.title}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </main>
      
      <Footer />
    </div>
  );
};

export default MyBoards;
